import { useMemo } from "react";
import { useApiConfig } from "common-fe";
import { Configuration, RiderSheetsApi, FederationApi } from "report-common";

// #846 — score-admin's first direct report-api client (mirrors scoreApi.js).
// `useApiConfig` (common-fe) stamps the Cognito bearer token AND the
// active-org `X-Org-Id` header (from the mounted ActiveOrgProvider, already
// wrapped around the app — see ReportsPage.jsx) onto every request. Base URL
// comes from VITE_REPORT_API_BASE_URL (already set up for ReportsPage's
// basePath prop). The server-error interceptor is installed once, globally,
// by scoreApi.js's module load — no need to re-install it here.
const REPORT_API_BASE_URL = import.meta.env.VITE_REPORT_API_BASE_URL;

export function useReportApi() {
  const buildConfig = useApiConfig();
  return useMemo(() => {
    const config = new Configuration(buildConfig(REPORT_API_BASE_URL));
    return {
      riderSheets: new RiderSheetsApi(config),
      // #751 — federation (USEF/USDF) results-publishing endpoints.
      federation: new FederationApi(config),
    };
  }, [buildConfig]);
}

// JSend unwrap: report-api wraps every success as { status: "success", data }.
export function unwrapReport(response) {
  return response?.data?.data;
}

// #751 — federation routes 400/409/502 with a JSend failure envelope:
// { status: "fail", data: {message, issues?, issuesByClass?, ...}, code }
// (code is a top-level sibling of data, per common-api's createFailureResponse).
// Axios rejects on any non-2xx, so every federation action needs to normalize
// err.response.data into one shape: a curated message, the verbatim USEF
// issues[]/issuesByClass{} (rendered as-is per the card's AC — never
// re-worded), and the machine `code` the UI keys illegal-action disabling off.
export function federationErrorFrom(err) {
  const body = err?.response?.data;
  if (!body) {
    return {
      code: "network_error",
      message: err?.message || "Request failed",
      issues: null,
      issuesByClass: null,
      raw: null,
    };
  }
  return {
    code: body.code || null,
    message: body.data?.message || "Request failed",
    issues: Array.isArray(body.data?.issues) ? body.data.issues : null,
    issuesByClass:
      body.data?.issuesByClass && typeof body.data.issuesByClass === "object"
        ? body.data.issuesByClass
        : null,
    raw: body.data,
  };
}
